/**
 * API para gestión de asignación de estudiantes a grupos
 * Tabla: esc_estudiantes_grupos
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const API_URL = `${API_BASE}/escolar`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

function notifyEstudiantesGruposUpdated(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent('estudiantes-grupos:updated', { detail }));
  } catch (_) {}
}

/**
 * Obtener todos los grupos asignados a un estudiante
 * @param {number} idEstudiante - ID del estudiante
 * @param {object} params - Parámetros adicionales de filtrado
 * @returns {Promise<Array>} Lista de grupos asignados
 */
export async function getGruposEstudiante(idEstudiante, params = {}) {
  try {
    let url = `${API_URL}/estudiantes-grupos/?id_estudiante=${idEstudiante}`;
    
    if (params.id_ciclo_escolar) {
      url += `&id_ciclo_escolar=${params.id_ciclo_escolar}`;
    }
    if (params.estado) {
      url += `&estado=${params.estado}`;
    }
    if (params.activo !== undefined) {
      url += `&activo=${params.activo}`;
    }
    
    console.log('[estudiantesGrupos.js] Llamando a:', url);
    
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!res.ok) {
      console.error('[estudiantesGrupos.js] Error en respuesta:', res.status);
      return [];
    }
    
    const data = await res.json();
    console.log('[estudiantesGrupos.js] Datos recibidos:', data);
    
    // Mapear los datos para la UI
    if (Array.isArray(data)) {
      return data.map(item => ({
        id: item.id_estudiante_grupo,
        id_estudiante_grupo: item.id_estudiante_grupo,
        id_estudiante: item.id_estudiante,
        id_grupo: item.id_grupo,
        id_materia: item.id_materia,
        id_inscripcion: item.id_inscripcion,
        id_ciclo_escolar_grupo: item.id_ciclo_escolar_grupo,
        fecha_asignacion: item.fecha_asignacion,
        tipo_asignacion: item.tipo_asignacion,
        estado: item.estado_estudiante_grupo,
        activo: item.activo,
        // Información del grupo
        grupo_codigo: item.grupo_codigo || 'N/A',
        grupo_modalidad: item.grupo_modalidad || 'N/A',
        grupo_estado: item.grupo_estado || 'N/A',
        // Información de la materia
        materia_nombre: item.materia_nombre || 'N/A',
        materia_creditos: item.materia_creditos || 'N/A',
        materia_codigo: item.materia_codigo || 'N/A',
        // Información del asesor
        asesor_nombre: item.asesor_nombre || 'N/A',
        // Información del ciclo
        ciclo_nombre: item.ciclo_nombre || 'N/A',
        // Horarios
        horarios: item.horarios || [],
        // Formato de horario legible
        horario_texto: formatHorarios(item.horarios || []),
        // Calificaciones (fuente esc_calificaciones) - usadas por
        // AsignacionGruposScreen para detectar materias reprobadas.
        calificacion_final: item.calificacion_final ?? null,
        calificacion_extraordinaria: item.calificacion_extraordinaria ?? null,
        mejor_calificacion: item.mejor_calificacion ?? null,
        tiene_calificacion_final: !!item.tiene_calificacion_final,
        aprobada: item.aprobada, // true | false | null
      }));
    }
    
    return [];
  } catch (err) {
    console.error('[estudiantesGrupos.js] Error al cargar grupos del estudiante:', err);
    return [];
  }
}

/**
 * Formatear horarios para mostrar en la UI
 * @param {Array} horarios - Array de objetos de horario
 * @returns {string} Texto formateado de horarios
 */
function formatHorarios(horarios) {
  if (!horarios || horarios.length === 0) {
    return 'N/A';
  }
  
  return horarios.map(h => {
    const dia = h.dia || 'N/A';
    const horaInicio = h.hora_inicio || '';
    const horaFin = h.hora_fin || '';
    return `${dia} ${horaInicio}-${horaFin}`;
  }).join(', ');
}

/**
 * Obtener el ciclo actual y el SIGUIENTE ciclo escolar de un estudiante.
 * Reutiliza la lógica institucional del backend (misma que Preasignación).
 * @param {number} idEstudiante - ID del estudiante
 * @returns {Promise<{ciclo_actual:Object|null, ciclo_siguiente:Object|null}>}
 */
export async function getSiguienteCicloEstudiante(idEstudiante) {
  try {
    const url = `${API_URL}/estudiantes/${idEstudiante}/siguiente-ciclo/`;
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) {
      console.warn('[estudiantesGrupos.js] siguiente-ciclo status', res.status);
      return { ciclo_actual: null, ciclo_siguiente: null };
    }
    const data = await res.json();
    return {
      ciclo_actual: data.ciclo_actual || null,
      ciclo_siguiente: data.ciclo_siguiente || null,
    };
  } catch (err) {
    console.error('[estudiantesGrupos.js] Error al obtener siguiente ciclo:', err);
    return { ciclo_actual: null, ciclo_siguiente: null };
  }
}

/**
 * Obtener todos los estudiantes de un grupo
 * @param {number} idGrupo - ID del grupo
 * @returns {Promise<Array>} Lista de estudiantes asignados al grupo
 */
export async function getEstudiantesGrupo(idGrupo) {
  try {
    const url = `${API_URL}/estudiantes-grupos/?id_grupo=${idGrupo}`;
    console.log('[estudiantesGrupos.js] Llamando a:', url);
    
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!res.ok) {
      console.error('[estudiantesGrupos.js] Error en respuesta:', res.status);
      return [];
    }
    
    const data = await res.json();
    console.log('[estudiantesGrupos.js] Datos recibidos:', data);
    
    if (Array.isArray(data)) {
      return data.map(item => ({
        id: item.id_estudiante_grupo,
        id_estudiante: item.id_estudiante,
        nombre: item.estudiante_nombre || 'N/A',
        matricula: item.estudiante_matricula || 'N/A',
        estado: item.estado_estudiante_grupo,
        fecha_asignacion: item.fecha_asignacion,
        tipo_asignacion: item.tipo_asignacion
      }));
    }
    
    return [];
  } catch (err) {
    console.error('[estudiantesGrupos.js] Error al cargar estudiantes del grupo:', err);
    return [];
  }
}

/**
 * Asignar un estudiante a un grupo
 * @param {object} data - Datos de la asignación
 * @returns {Promise<object>} Resultado de la operación
 */
export async function asignarEstudianteGrupo(data) {
  try {
    const payload = {
      id_estudiante: data.id_estudiante,
      id_grupo: data.id_grupo,
      fecha_asignacion: data.fecha_asignacion || new Date().toISOString().split('T')[0],
      tipo_asignacion: data.tipo_asignacion || 'MANUAL',
      estado_estudiante_grupo: data.estado || 'INSCRITO',
      activo: true
    };
    
    // Solo incluir id_inscripcion si tiene valor
    if (data.id_inscripcion) {
      payload.id_inscripcion = data.id_inscripcion;
    }
    
    console.log('[estudiantesGrupos.js] Asignando estudiante a grupo:', payload);
    
    const res = await fetch(`${API_URL}/estudiantes-grupos/`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        ...getAuthHeaders() 
      },
      body: JSON.stringify(payload)
    });
    
    const result = await res.json();
    
    // Exitoso: códigos 200, 201
    if (res.ok) {
      console.log('[estudiantesGrupos.js] Asignación exitosa:', result);
      // Notificar incluso si ya existía (operación idempotente)
      notifyEstudiantesGruposUpdated({
        action: result.ya_existia ? 'reactivar' : 'asignar',
        id_estudiante: data.id_estudiante,
        id_grupo: data.id_grupo,
        mensaje: result.mensaje
      });
      return result;
    } else {
      // Error (400, 500, etc.)
      console.error('[estudiantesGrupos.js] Error en respuesta:', res.status, result);
      const errorMsg = result.detail || result.error || 'Error al asignar estudiante a grupo';
      throw new Error(errorMsg);
    }
  } catch (err) {
    console.error('[estudiantesGrupos.js] Error al asignar estudiante:', err);
    throw err;
  }
}

/**
 * Desasignar (soft delete) un estudiante de un grupo
 * Usa el endpoint PATCH /estudiantes-grupos/{id}/desasignar/
 * @param {number} idEstudianteGrupo - ID de la asignación
 * @param {number} idEstudiante - ID del estudiante (validación de seguridad)
 * @returns {Promise<object>} Resultado de la operación
 */
export async function desasignarEstudianteGrupo(idEstudianteGrupo, idEstudiante) {
  try {
    console.log('[estudiantesGrupos.js] Desasignando:', { idEstudianteGrupo, idEstudiante });
    
    const res = await fetch(`${API_URL}/estudiantes-grupos/${idEstudianteGrupo}/desasignar/`, {
      method: 'PATCH',
      headers: { 
        'Content-Type': 'application/json', 
        ...getAuthHeaders() 
      },
      body: JSON.stringify({ id_estudiante: idEstudiante })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      console.error('[estudiantesGrupos.js] Error al desasignar:', data);
      throw new Error(data.detail || 'Error al desasignar estudiante del grupo');
    }
    
    console.log('[estudiantesGrupos.js] Desasignación exitosa:', data);
    notifyEstudiantesGruposUpdated({
      action: 'desasignar',
      id_estudiante: idEstudiante,
      id_estudiante_grupo: idEstudianteGrupo,
      id_grupo: data?.id_grupo
    });
    return data;
  } catch (err) {
    console.error('[estudiantesGrupos.js] Error al desasignar estudiante:', err);
    throw err;
  }
}

/**
 * Eliminar asignación de estudiante a grupo (hard delete - uso legacy)
 * @param {number} idEstudianteGrupo - ID de la asignación
 * @returns {Promise<void>}
 */
export async function eliminarAsignacion(idEstudianteGrupo) {
  try {
    const res = await fetch(`${API_URL}/estudiantes-grupos/${idEstudianteGrupo}/`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!res.ok) {
      throw new Error('Error al eliminar asignación');
    }
    
    return true;
  } catch (err) {
    console.error('[estudiantesGrupos.js] Error al eliminar asignación:', err);
    throw err;
  }
}

export default {
  getGruposEstudiante,
  getEstudiantesGrupo,
  asignarEstudianteGrupo,
  desasignarEstudianteGrupo,
  eliminarAsignacion
};
